import has from 'lodash/has';
import isEqual from 'lodash/isEqual';

type FinishableFunction<T, Args extends unknown[]> = {
  finished: boolean;
  args: Args;
  lastValue: T | undefined;
  error: unknown;
  promise: Promise<T> | undefined;
  reset: () => void;
  (...args: Args): Promise<T>;
};

export function isFinishableFunction<T, Args extends unknown[]>(
  call: (...args: Args) => Promise<T>
): call is FinishableFunction<T, Args> {
  return has(call, 'finished') && has(call, 'lastValue') && has(call, 'promise') && has(call, 'reset');
}

export function singletonPromise<T, Args extends unknown[]>(
  call: (...args: Args) => Promise<T>
): FinishableFunction<T, Args> {
  const func: FinishableFunction<T, Args> = (...args: Args) => {
    const existingPromise = func.promise;
    if (existingPromise !== undefined && isEqual(args, func.args)) {
      return existingPromise;
    }
    func.args = args;
    const promise = (async () => {
      try {
        const returnValue = await call(...args);
        func.lastValue = returnValue;
        return returnValue;
      } catch (e) {
        func.error = e;
        throw e;
      } finally {
        func.finished = true;
      }
    })();
    func.promise = promise;
    return promise;
  };
  func.finished = false;
  func.args = [] as unknown as Args;
  func.error = undefined;
  func.promise = undefined;
  func.lastValue = undefined;
  func.reset = () => {
    if (func.finished) {
      func.finished = false;
      func.args = [] as unknown as Args;
      func.error = undefined;
      func.promise = undefined;
      func.lastValue = undefined;
    }
  };
  return func;
}
